import {
  Dialog,
  DialogContent,
  DialogTitle,
  FormGroup,
  Switch,
  Button,
  FormControlLabel,
} from "@mui/material";
import Form from "../../../components/common/Form/Form";
import { z } from "zod";
import {
  FeatureFlagFormValues,
  DEFAULT_FEATURE_FORM_VALUES,
} from "../../../api/featureFlag";
import { Controller, SubmitHandler } from "react-hook-form";
import { DateTimePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import FeatureFlag from "../../../types/feature-flags/featureFlag";
import LoadingIndicator from "../../../components/common/LoadingIndicator/LoadingIndicator";
import { useTranslation } from "react-i18next";

const schema = z.object({
  isEnabled: z.boolean(),
  expiration: z.union([z.string().datetime(), z.null()]),
});

export type FeatureFlagDialogProps = {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  formValues?: FeatureFlagFormValues;
  selectedRow: FeatureFlag;
  editSite: (
    customerId: string,
    featureName: string,
    data: FeatureFlagFormValues
  ) => Promise<void>;
};

export default function FeatureFlagDialog({
  isOpen,
  setIsOpen,
  formValues,
  selectedRow,
  editSite,
}: Readonly<FeatureFlagDialogProps>) {
  const { t } = useTranslation(["featureFlags", "common"]);

  const onSubmit: SubmitHandler<FeatureFlagFormValues> = (data) => {
    editSite(selectedRow.customerId, selectedRow.featureName, data);
  };

  return (
    <Dialog
      open={isOpen}
      onClose={() => setIsOpen(false)}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>{t("featureFlags:editFeatureFlag")}</DialogTitle>
      <DialogContent>
        <Form
          id="edit-feature-flag"
          onSubmit={onSubmit}
          schema={schema}
          options={{
            defaultValues: formValues || DEFAULT_FEATURE_FORM_VALUES,
          }}
        >
          {({ formState, control }) => {
            const { isSubmitting, isDirty, isLoading } = formState;
            return (
              <>
                <FormGroup>
                  <Controller
                    name="isEnabled"
                    control={control}
                    render={({ field }) => (
                      <FormControlLabel
                        control={
                          <Switch
                            checked={field.value ?? false}
                            onChange={(e) => field.onChange(e.target.checked)}
                          />
                        }
                        label={t("featureFlags:featureEnabled")}
                      />
                    )}
                  />
                </FormGroup>
                <FormGroup>
                  <Controller
                    name="expiration"
                    control={control}
                    render={({ field }) => (
                      <DateTimePicker
                        label={t("common:expirationDate")}
                        minDateTime={dayjs()}
                        value={field.value ? dayjs(field.value) : null}
                        views={[
                          "year",
                          "month",
                          "day",
                          "hours",
                          "minutes",
                          "seconds",
                        ]}
                        onChange={(newValue) => {
                          field.onChange(
                            newValue?.isValid() ? newValue.toISOString() : null
                          );
                        }}
                      />
                    )}
                  />
                </FormGroup>
                <div>
                  <Button
                    variant="contained"
                    type="submit"
                    fullWidth
                    disabled={isSubmitting || !isDirty || isLoading}
                  >
                    {isSubmitting ? <LoadingIndicator /> : t("common:save")}
                  </Button>
                </div>
              </>
            );
          }}
        </Form>
      </DialogContent>
    </Dialog>
  );
}
